import { useRef } from 'react';
import { SUPPORTED_LANGUAGES, getLanguageName } from '@/utils/languages';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { TagsMultiSelect } from '@/components/ui/TagsMultiSelect';
import { useLabelOptions } from '@/hooks/useLabelOptions';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useDrawerFocus } from '@/hooks/useDrawerFocus';

/**
 * VariationFilterDrawer Component
 *
 * Slide-out drawer from the right with filter controls:
 * - Search input
 * - Type filter (All, Language, Custom)
 * - Status filter (All, Draft, Ready)
 * - Language filter
 * - Workflow Status filter
 * - Parent Post filter
 * - Labels filter (shared TagsMultiSelect + useLabelOptions, as in the Creator)
 * - Apply and Clear filters buttons
 *
 * Features:
 * - Slides in from right
 * - Dark backdrop overlay
 * - Closes on ESC key
 * - Closes on backdrop click
 * - Focus trap when open
 * - Body scroll lock when open
 */

interface VariationFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    search: string;
    type: 'all' | 'language' | 'custom';
    status: 'all' | 'DRAFT' | 'READY';
    language: string;
    workflowStatus: string;
    labels: string;
  };
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  workflowEnabled?: boolean;
}

export const VariationFilterDrawer = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  workflowEnabled = false,
}: VariationFilterDrawerProps) => {
  const { t } = useTranslation('common');
  const drawerRef = useRef<HTMLDivElement>(null);

  // Move constant arrays INSIDE component so they can access t()
  const TYPE_OPTIONS = [
    { value: 'all', label: t('all_types'), icon: ICONS.FOLDER },
    { value: 'language', label: t('language'), icon: ICONS.LANGUAGE },
    { value: 'custom', label: t('custom'), icon: ICONS.EDIT },
  ];

  const STATUS_OPTIONS = [
    { value: 'all', label: t('all_status'), icon: ICONS.FOLDER },
    { value: 'DRAFT', label: t('draft'), icon: ICONS.EDIT },
    { value: 'READY', label: t('ready'), icon: ICONS.CHECK_CIRCLE },
  ];

  const LANGUAGE_OPTIONS = [
    { value: 'all', label: t('all_languages', { defaultValue: 'All Languages' }), icon: ICONS.LANGUAGE },
    ...SUPPORTED_LANGUAGES.map((code) => ({
      value: code,
      label: getLanguageName(code),
      icon: ICONS.LANGUAGE,
    })),
  ];

  const WORKFLOW_STATUS_OPTIONS = [
    { value: 'all', label: t('all_status', { defaultValue: 'All' }), icon: ICONS.FOLDER },
    { value: 'PENDING', label: t('pending', { defaultValue: 'Pending Review' }), icon: ICONS.CLOCK },
    { value: 'APPROVED', label: t('approved', { defaultValue: 'Released' }), icon: ICONS.CHECK_CIRCLE },
    { value: 'CHANGES_REQUESTED', label: t('changes_requested', { defaultValue: 'Changes Requested' }), icon: ICONS.WARNING },
  ];


  // Same canonical options as the Creator filter (only fetched while open)
  const { data: labelOptions } = useLabelOptions({ enabled: isOpen });
  const availableLabels = labelOptions || [];
  const selectedLabels = filters.labels ? filters.labels.split(',').filter(Boolean) : [];

  const hasActiveFilters =
    filters.labels !== '' ||
    filters.search !== '' ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    filters.language !== 'all' ||
    filters.workflowStatus !== 'all';

  /**
   * ESC closes this drawer only when it is the topmost layer and no inner popover consumed the key.
   */
  useEscapeLayer(isOpen, onClose);

  /**
   * Prevent body scroll when drawer is open
   */
  useBodyScrollLock(isOpen);

  // Inert + aria-hidden while closed; focus in on open, restore to the opener on close
  useDrawerFocus(isOpen, drawerRef);

  return (
    <div
      className={`fixed inset-0 z-40 transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed right-0 top-navbar-height bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 w-full sm:max-w-md bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby="variation-filter-drawer-title"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.FILTER} size="sm" color="text-gray-600" />
            <h2 id="variation-filter-drawer-title" className="text-sm lg:text-lg font-bold text-gray-900">
              {t('filter_variations')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('close_filter_drawer')}
          >
            <Icon name={ICONS.CROSS} size="sm" color="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Search */}
          <div>
            <Input
              inputSize="sm"
              type="search"
              id="variation-search-filter"
              placeholder={t('search_variations')}
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="w-full"
            />
          </div>

          {/* Workflow Status Filter — shown near the top for quick access (agency teams only) */}
          {workflowEnabled && (
            <div>
              <Select
                size="sm"
                label={t('workflow_status', { defaultValue: 'Workflow Status' })}
                value={filters.workflowStatus}
                onValueChange={(value) => onFilterChange('workflowStatus', value)}
                options={WORKFLOW_STATUS_OPTIONS}
                className="w-full"
              />
            </div>
          )}

          {/* Type Filter */}
          <div>
            <Select
              size="sm"
              label={t('type')}
              value={filters.type}
              onValueChange={(value) => onFilterChange('type', value)}
              options={TYPE_OPTIONS}
              className="w-full"
            />
          </div>

          {/* Status Filter */}
          <div>
            <Select
              size="sm"
              label={t('status')}
              value={filters.status}
              onValueChange={(value) => onFilterChange('status', value)}
              options={STATUS_OPTIONS}
              className="w-full"
            />
          </div>

          {/* Language Filter */}
          <div>
            <Select
              size="sm"
              label={t('language', { defaultValue: 'Language' })}
              value={filters.language}
              onValueChange={(value) => onFilterChange('language', value)}
              options={LANGUAGE_OPTIONS}
              className="w-full"
            />
          </div>

          {/* Labels Filter (same control as the Creator filter) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('labels')}
            </label>
            <TagsMultiSelect
              size="sm"
              value={selectedLabels}
              onChange={(tags: string[]) => onFilterChange('labels', tags.join(','))}
              availableTags={availableLabels.map((label) => ({
                name: label.name,
                count: label.count,
              }))}
              placeholder={t('filter_by_labels')}
              className="w-full"
            />
          </div>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 pb-6 sm:pb-4 flex gap-1.5 sm:gap-3">
          <Button
            variant="primary"
            onClick={onClose}
            className="flex-1 w-full"
          >
            {t('apply_filters')}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={() => {
                onClearFilters();
                onClose();
              }}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Icon name={ICONS.CROSS} size="sm" />
              {t('clear_all')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
